import { useDepartmentStore, type CampaignData } from "@/features/department";
import { PRODUCT_METRICS_NEEDED } from "@/store/department/pipelines/ecommerceHighlights";

import type { LogFn } from "@/features/companion/lib/companionLogger";
import { dispatchBrandTool } from "@/features/brand";
import { dispatchGeoTool } from "./dispatchers/geoDispatcher";
import { dispatchGrowthTool } from "./dispatchers/growthDispatcher";
import { dispatchSocialTool } from "./dispatchers/socialDispatcher";
import { dispatchAdsTool } from "@/features/ads";
import { dispatchContentStudioTool } from "@/features/content";
import { asRecord } from "./dispatchers/types";
import { appEvents } from "@/shared/lib/events";

interface DispatchCompanionToolCallOptions {
  cpLog?: LogFn;
  source?: string;
}

export function dispatchCompanionToolCall(
  toolName: string,
  input: unknown,
  options: DispatchCompanionToolCallOptions = {},
) {
  const { cpLog, source = "unknown" } = options;
  const payload = asRecord(input);
  const ctx = { payload, cpLog, source };

  // Try domain-specific dispatchers first
  if (dispatchBrandTool(toolName, ctx)) return;
  if (dispatchGeoTool(toolName, ctx)) return;
  if (dispatchGrowthTool(toolName, ctx)) return;
  if (dispatchSocialTool(toolName, ctx)) return;
  if (dispatchAdsTool(toolName, ctx)) return;
  if (dispatchContentStudioTool(toolName, ctx)) return;

  // Generic/pipeline tools
  const state = useDepartmentStore.getState();

  switch (toolName) {
    case "run_pipeline":
    case "run_node": {
      const targetNodeId = (payload.startNodeId ?? payload.nodeId) as string | undefined;
      if (!targetNodeId) {
        cpLog?.("node-forward-missing", `source=${source} tool=${toolName}`);
        return;
      }

      const autoProceed = toolName === "run_pipeline";
      cpLog?.(
        "node-forward",
        `source=${source} tool=${toolName} node=${targetNodeId} autoProceed=${autoProceed}`,
      );
      state.addStepLog(
        state.currentStepIndex,
        `[NODE] Forward requested via ${toolName} -> node ${targetNodeId}`,
      );
      appEvents.publish({
        type: "agent-run-node",
        nodeId: targetNodeId,
        autoProceed,
      });
      return;
    }
    case "start_coagent_research":
      appEvents.publish({
        type: "agent-start-research",
        brandInput: payload.brandInput as unknown,
      });
      return;
    case "advance_step":
      // Phase 10-7c: brand-DNA step state is artifact-authoritative
      // server-side. The agent (Remi + sub-agents) no longer mutates
      // step state — the wizard advances when artifacts land. For
      // brand-dna treat this as a no-op so any stale chat history that
      // still calls it (or any non-Remi agent that fires it) doesn't
      // race the server's hydrate. Other co-agents (geo, ads, growth,
      // ecommerce) keep the legacy behavior.
      if (state.coAgentType === "brand-dna") {
        cpLog?.("step-advance-noop", `source=${source} (brand-dna artifact-authoritative)`);
        return;
      }
      cpLog?.(
        "step-advance",
        `source=${source} nextStatus=${(payload.nextStatus as string | undefined) || "waiting"}`,
      );
      state.clearWidgetHighlight(PRODUCT_METRICS_NEEDED.widgetId);
      state.completeCurrentStepAndAdvance(
        (payload.nextStatus as "running" | "waiting" | undefined) || "waiting",
      );
      return;
    case "set_step":
      if (state.coAgentType === "brand-dna") {
        cpLog?.("step-set-noop", `source=${source} (brand-dna artifact-authoritative)`);
        return;
      }
      cpLog?.(
        "step-set",
        `source=${source} step=${String(payload.stepIndex)} status=${(payload.status as string | undefined) || "waiting"}`,
      );
      state.setExecutionStep(
        Number(payload.stepIndex ?? 0),
        (payload.status as "running" | "waiting" | undefined) || "waiting",
      );
      return;
    case "update_campaign_data":
      state.updateCampaignData(
        ((payload.updates as Partial<CampaignData>) || {}) as Partial<CampaignData>,
      );
      return;
    case "run_coagent_step":
      cpLog?.(
        "step-run",
        `source=${source} step=${String(payload.step)} userInputKeys=${Object.keys(asRecord(payload.userInput)).length}`,
      );
      state.callStep(
        Number(payload.step ?? 0),
        asRecord(payload.userInput) as Record<string, unknown>,
      );
      return;
    case "highlight_widget": {
      const widgetId = typeof payload.widgetId === "string" ? payload.widgetId : "";
      const comment = typeof payload.comment === "string" ? payload.comment : "";
      const duration = typeof payload.durationMs === "number" ? payload.durationMs : 8000;
      const stepIndex = typeof payload.stepIndex === "number" ? payload.stepIndex : null;
      const chatMessage = typeof payload.chatMessage === "string" ? payload.chatMessage : "";
      const actionLabel = typeof payload.actionLabel === "string" ? payload.actionLabel : undefined;
      if (!widgetId) {
        cpLog?.("highlight-skip", `source=${source} reason=missing-widgetId`);
        return;
      }
      cpLog?.("highlight-widget", `source=${source} widget=${widgetId} step=${String(stepIndex)}`);
      if (stepIndex !== null) {
        state.setFocusedStep(stepIndex);
      }
      if (chatMessage && state.coAgentType !== "products") {
        state.addMessage({
          role: "agent",
          content: chatMessage,
          source: "tool_dispatch",
          dedupeKey: `highlight:${widgetId}:chat_message`,
        });
      }
      state.highlightWidget(widgetId, comment, duration, actionLabel, stepIndex ?? undefined);
      return;
    }
    default:
      return;
  }
}
